<script setup lang="ts">
import { Head, Link, useForm } from "@inertiajs/vue3"

import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { outingPath, outingsPath } from "@/routes"
import type { OutingSummary } from "@/types/models"

defineProps<{
  outings: OutingSummary[]
}>()

const { title } = useNavbar({ title: "Outings" })
const form = useForm({ name: "" })

function createOuting() {
  form.post(outingsPath(), {
    onSuccess: () => form.reset(),
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <form class="flex items-end gap-2" @submit.prevent="createOuting">
      <UFormField label="New outing" required class="min-w-0 flex-1" :error="form.errors.name">
        <UInput
          v-model="form.name"
          name="name"
          placeholder="Weekend gig"
          autocomplete="off"
          required
          class="w-full"
        />
      </UFormField>
      <UButton type="submit" label="Create" :icon="ICON.add" :loading="form.processing" />
    </form>

    <ul v-if="outings.length" class="divide-default divide-y">
      <li v-for="outing in outings" :key="outing.id">
        <Link :href="outingPath(outing.id)" class="flex min-h-14 items-center gap-3 py-3">
          <UIcon :name="ICON.outing" class="text-muted size-5 shrink-0" />
          <div class="min-w-0 flex-1">
            <p class="text-highlighted truncate font-medium">{{ outing.name }}</p>
            <p class="text-muted text-xs">
              {{ outing.location || "No location" }} · {{ outing.returned_count }}/{{
                outing.item_count
              }}
              returned
            </p>
          </div>
          <UBadge color="neutral" variant="subtle">{{ outing.status }}</UBadge>
          <UIcon :name="ICON.chevronRight" class="text-muted size-4" />
        </Link>
      </li>
    </ul>

    <UEmpty
      v-else
      :icon="ICON.outing"
      title="No outings yet"
      description="Create one to start packing a temporary away-from-home checklist."
    />
  </NarrowPage>
</template>
